import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { getOrderFormProductsRequest } from '../../features/catalog/catalogApi';
import { addOrderProduct } from '../../features/deliveries/orderFormProducts';

const SEARCH_DELAY_MS = 300;

const inputClass =
  'w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors disabled:opacity-60';

/**
 * The SKUs of a single order, picked out of the seller's catalogue.
 *
 * A search box over the company's active products (SKU, name or barcode), and
 * under it the picked ones with a quantity each. Only catalogue products can
 * be added: the backend refuses anything else, so a typo cannot create a
 * product or reserve stock for one.
 *
 * The parent owns the list (`value`), so that it can empty it when the seller
 * changes, and runs `validateOrderProducts` on submit; this component only
 * shows the per-SKU messages it is handed.
 *
 * @param {object} props
 * @param {number|string|null} props.sellerId - Whose catalogue. A seller
 *   account passes nothing: the backend serves its own.
 * @param {boolean} props.enabled - False until the form knows the seller and
 *   the warehouse; the search stays closed and says why.
 * @param {Array<object>} props.value - The picked products (see
 *   `features/deliveries/orderFormProducts`).
 * @param {(products: Array<object>) => void} props.onChange
 * @param {Object<string, string>} [props.errors] - Message by SKU.
 */
export const OrderProductsPicker = ({ sellerId, enabled, value, onChange, errors = {} }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const latestRequest = useRef(0);

  // A new seller means a new catalogue: whatever was typed is about another.
  useEffect(() => {
    setSearch('');
    setResults(null);
    setSearchError(null);
  }, [sellerId, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const term = search.trim();
    if (!term) {
      setResults(null);
      setSearching(false);
      return undefined;
    }

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setSearching(true);
    setSearchError(null);

    const timer = setTimeout(async () => {
      try {
        const params = { search: term };
        if (sellerId) params.seller_id = sellerId;
        const response = await getOrderFormProductsRequest(params);
        if (latestRequest.current !== requestId) return;
        setResults(response.data?.results || []);
      } catch (error) {
        if (latestRequest.current !== requestId) return;
        setResults([]);
        setSearchError('No se pudo buscar en el catálogo. Intenta de nuevo.');
      } finally {
        if (latestRequest.current === requestId) setSearching(false);
      }
    }, SEARCH_DELAY_MS);

    return () => clearTimeout(timer);
  }, [search, sellerId, enabled]);

  const pickedSkus = new Set(value.map((entry) => entry.sku));
  const candidates = (results || []).filter((product) => !pickedSkus.has(product.sku));

  const add = (product) => {
    onChange(addOrderProduct(value, product));
    setSearch('');
    setResults(null);
  };

  const setQuantity = (sku, quantity) => {
    onChange(value.map((entry) => (entry.sku === sku ? { ...entry, quantity } : entry)));
  };

  const remove = (sku) => onChange(value.filter((entry) => entry.sku !== sku));

  return (
    <div data-testid="order-products-picker" className="space-y-3">
      <div>
        <label htmlFor="order-products-search" className="block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5">
          Productos (opcional)
        </label>
        <p className="text-xs text-text-secondary mb-2">
          Busca por SKU o nombre en el catálogo de la empresa y agrega los productos de la orden con su cantidad.
        </p>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {searching ? (
              <Loader2 size={16} className="animate-spin text-text-muted" />
            ) : (
              <Search size={16} className="text-text-muted" />
            )}
          </div>
          <input
            id="order-products-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            disabled={!enabled}
            placeholder={enabled ? 'Buscar producto...' : 'Primero selecciona el seller y la bodega'}
            autoComplete="off"
            className={`${inputClass} pl-9`}
          />
        </div>

        {searchError && <p className="text-danger text-xs mt-1">{searchError}</p>}

        {enabled && results !== null && !searching && !searchError && (
          <div
            data-testid="order-products-results"
            className="mt-2 border border-border rounded-xl overflow-hidden divide-y divide-border max-h-56 overflow-y-auto"
          >
            {candidates.length === 0 ? (
              <p className="px-4 py-3 text-sm text-text-secondary">
                {results.length === 0
                  ? 'Sin resultados en el catálogo de la empresa.'
                  : 'Todos los resultados ya están en la orden.'}
              </p>
            ) : (
              candidates.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => add(product)}
                  aria-label={`Agregar ${product.sku}`}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-ninja-gray/60 dark:hover:bg-white/5 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ninja-dark dark:text-white truncate">{product.name}</p>
                    <p className="text-xs text-text-muted font-mono">{product.sku}</p>
                  </div>
                  <Plus size={16} className="text-ninja-green shrink-0" />
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div
          data-testid="order-products-list"
          className="border border-border rounded-xl overflow-hidden divide-y divide-border"
        >
          {value.map((entry) => (
            <div key={entry.sku} data-testid={`order-product-${entry.sku}`} className="flex items-center gap-3 px-4 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ninja-dark dark:text-white truncate">{entry.name}</p>
                <p className="text-xs text-text-muted font-mono">{entry.sku}</p>
              </div>
              <div className="w-24">
                <input
                  type="number"
                  min="1"
                  step="1"
                  aria-label={`Cantidad de ${entry.sku}`}
                  value={entry.quantity}
                  onChange={(event) => setQuantity(entry.sku, event.target.value)}
                  className={`w-full text-center bg-transparent border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-ninja-green transition-colors ${
                    errors[entry.sku] ? 'border-danger' : 'border-border'
                  }`}
                />
                {errors[entry.sku] && <p className="text-danger text-[11px] mt-1 leading-tight">{errors[entry.sku]}</p>}
              </div>
              <button
                type="button"
                onClick={() => remove(entry.sku)}
                aria-label={`Quitar ${entry.sku}`}
                className="p-2 rounded-lg text-text-muted hover:text-danger hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
